import React from 'react'

import { GrStatusCritical, GrCircleInformation, GrStatusWarning } from 'react-icons/gr'

import type { HeaderConfig } from '@/components/ui'

// Configuration object that maps severity names to their display properties
const SEVERITY_CONFIGS = {
  critical: {
    key: 'critical',
    title: 'Critical',
    icon: <GrStatusCritical className="w-4 h-4 text-[#eb5454]" />,
    // color: 'text-error',
  },
  warning: {
    key: 'warning',
    title: 'Warning',
    icon: <GrStatusWarning className="w-4 h-4 text-[#FFB627]" />,
    // color: 'text-warning',
  },
  informational: {
    key: 'informational',
    title: 'Info',
    icon: <GrCircleInformation className="w-4 h-4 text-[#47B262]" />,
    // color: 'text-info',
  },
} as const

const SeverityOrder = ['critical', 'warning', 'informational'] as SeverityName[]

// Type for valid severity names (derived from our config)
export type SeverityName = keyof typeof SEVERITY_CONFIGS

// Options for customizing the header builder
export interface SeverityHeaderOptions {
  /**
   * Array of severity names to include in headers
   * If empty or undefined, all severities will be included
   */
  selectedSeverities?: SeverityName[]

  /**
   * Whether to include icons in the headers
   * @default true
   */
  includeIcons?: boolean

  /**
   * Custom icon size classes
   * @default "w-4 h-4"
   */
  iconSize?: string

  /**
   * Whether to include a total column
   * @default true
   */
  includeTotal?: boolean
}

/**
 * Creates enhanced headers with severity icons for tables
 *
 * This is like a smart header factory that knows how to build
 * consistent, iconified headers for severity-based data tables.
 *
 * @param options Configuration options for customizing the headers
 * @returns Array of HeaderConfig objects ready for use with SimpleTable
 *
 * @example
 * ```typescript
 * // Basic usage - all severities with icons
 * const headers = createSeverityHeaders()
 *
 * // Only specific severities
 * const headers = createSeverityHeaders({
 *   selectedSeverities: ['critical', 'warning']
 * })
 *
 * // Without icons (just text)
 * const headers = createSeverityHeaders({
 *   includeIcons: false
 * })
 * ```
 */
export const createSeverityHeaders = (options: SeverityHeaderOptions = {}): HeaderConfig[] => {
  const { selectedSeverities = [], includeIcons = true, iconSize = 'w-4 h-4', includeTotal = true } = options

  // Start with the first column (usually asset/device name)
  const headers: HeaderConfig[] = []

  // Determine which severities to include
  const severityNames =
    selectedSeverities.length > 0
      ? SeverityOrder.filter((severity) => selectedSeverities.includes(severity))
      : (SeverityOrder as SeverityName[])

  // Add severity columns
  severityNames.forEach((severityName) => {
    const config = SEVERITY_CONFIGS[severityName]
    if (!config) {
      console.warn(`Unknown severity name: ${severityName}`)
      return
    }

    if (includeIcons) {
      // Clone the icon with custom size if needed
      const customIcon =
        iconSize !== 'w-4 h-4'
          ? React.cloneElement(config.icon, {
              className: `${iconSize}`,
            })
          : config.icon

      headers.push({
        key: config.key,
        title: config.title,
        icon: customIcon,
      })
    } else {
      // Just text headers without icons
      headers.push({
        key: config.key,
        title: config.title,
      })
    }
  })

  // Add total column if requested
  if (includeTotal) {
    headers.push({
      key: 'total',
      title: 'Total',
    })
  }

  return headers
}

/**
 * Get severity configuration by name
 * Useful for accessing individual severity properties
 */
export const getSeverityConfig = (severityName: SeverityName) => {
  return SEVERITY_CONFIGS[severityName]
}

/**
 * Get all available severity names
 * Useful for validation or building UI controls
 */
export const getAllSeverityNames = (): SeverityName[] => {
  return Object.keys(SEVERITY_CONFIGS) as SeverityName[]
}

/**
 * Helper to validate severity names
 * Returns only valid severity names from an input array
 */
export const validateSeverityNames = (names: string[]): SeverityName[] => {
  return names.filter((name) => name in SEVERITY_CONFIGS) as SeverityName[]
}
